import type { SpeicherZustand } from './speicher-persistenz';
import type { RohStamm, SyncZustand } from './sync';

/** P1-14: Hinweis im vorhandenen Feld `letzterFehler`, wenn der Offline-Puffer flüchtig ist. */
export const PUFFER_FLUECHTIG_HINWEIS =
  'Offline-Puffer nicht dauerhaft (kein IndexedDB) – Offline-Verkauf gesperrt';

/** Codex 7: dauerhafte Sicherung (localStorage) gescheitert. */
export const SCHREIBFEHLER_HINWEIS = 'Dauerhafte Sicherung gescheitert';

/**
 * Gerätestatus-Meldung der Kassen-App an den Server (P9, alle 60 s, ohne Geheimnisse).
 * Zusätzlich zur bisherigen Meldung die Übernahmequittung (P1): übernommene Stammversion und
 * das übernommene Schnellwahl-Profil (ID und Version; null = kein Profil). Daraus zeigt das
 * Backoffice je Veröffentlichung und Kasse „wartet / übernommen / Fehler“.
 */
export function geraetestatusMeldung(e: {
  sync: SyncZustand;
  offlineBelege: number;
  kuechenbons: number;
  stamm: RohStamm | null;
  /** P1-10/P1-14: dauerhafter Speicher und Art des Offline-Puffers (fehlt = nicht melden). */
  speicher?: SpeicherZustand;
}): Record<string, unknown> {
  const z = e.sync;
  const stamm = e.stamm;
  return {
    syncZustand: z.art,
    ...(z.art === 'synchron' || z.art === 'aktualisiert' ? { syncAm: z.am } : {}),
    ...(z.art === 'fehler'
      ? { letzterFehler: z.grund.slice(0, 200) }
      : e.speicher?.puffer === 'fluechtig'
        ? { letzterFehler: PUFFER_FLUECHTIG_HINWEIS }
        : (e.speicher?.schreibfehler?.length ?? 0) > 0
          ? {
              letzterFehler: `${SCHREIBFEHLER_HINWEIS}: ${(e.speicher?.schreibfehler ?? []).join(', ')}`,
            }
          : {}),
    ...(e.speicher === undefined ? {} : { speicher: e.speicher }),
    ...(stamm === null
      ? {}
      : {
          stammStand: stamm.syncStand,
          stammVersion: stamm.version,
          // undefined = Profil noch nicht geprüft → nichts melden (Server: „unbekannt“)
          ...(stamm.profil === undefined
            ? {}
            : {
                schnellwahl:
                  stamm.profil === null
                    ? null
                    : { profilId: stamm.profil.id, version: stamm.profil.version },
              }),
        }),
    rueckstaende: { offlineBelege: e.offlineBelege, kuechenbons: e.kuechenbons },
  };
}

/**
 * Felder, die ältere Server (strenges Schema) noch nicht kennen: Übernahmequittung (P1) und
 * Speicherzustand (Offline-Plan P1-10/P1-14).
 */
const QUITTUNGSFELDER = ['stammVersion', 'schnellwahl', 'speicher'] as const;

/**
 * Merkt sich je Kassen-App-Sitzung, ob der Server die Quittungsfelder ablehnt (ältere API),
 * damit nicht jede Minute zweimal gesendet wird.
 */
export interface StatusSendung {
  ohneQuittung: boolean;
}

/**
 * Gerätestatus senden, abwärtskompatibel: lehnt der Server die Meldung mit 400 ab und enthält
 * sie Quittungsfelder, einmal ohne diese erneut senden und die Felder für diese Sitzung
 * weglassen. Andere Fehler werden weitergereicht (der Aufrufer verschluckt sie wie bisher).
 */
export async function sendeGeraetestatus(
  senden: (daten: Record<string, unknown>) => Promise<void>,
  meldung: Record<string, unknown>,
  zustand: StatusSendung,
): Promise<void> {
  const ohne = (): Record<string, unknown> =>
    Object.fromEntries(
      Object.entries(meldung).filter(([k]) => !(QUITTUNGSFELDER as readonly string[]).includes(k)),
    );
  const mitQuittung = QUITTUNGSFELDER.some((k) => k in meldung);
  if (zustand.ohneQuittung || !mitQuittung) {
    await senden(zustand.ohneQuittung ? ohne() : meldung);
    return;
  }
  try {
    await senden(meldung);
  } catch (e: unknown) {
    const status = (e as { status?: unknown } | null)?.status;
    if (status !== 400) {
      throw e;
    }
    zustand.ohneQuittung = true;
    await senden(ohne());
  }
}
